// Helpers for rendering the nested-subaccount report lines emitted by the backend
// (Balance Sheet, Income Statement). Each parent with children appears as a header
// row (the parent account, with its own direct-posting balance), then the children,
// then a "Total <parent>" subtotal row at the same depth with the rolled-up amount.
//
// To support expand/collapse, we need to pair each parent header with its matching
// subtotal so we can hide the children-and-subtotal in one shot AND swap the parent's
// own-balance amount for the rolled-up total when it is collapsed.

export interface ReportLineLike {
  accountId: string;
  isSubtotal?: boolean;
  depth?: number;
  name: string;
  amount: string;
}

export interface ParentInfo {
  /** Index of the matching "Total <parent>" subtotal row. */
  subtotalIdx: number;
  /** Rolled-up amount (the subtotal's amount) — shown on the parent row when collapsed. */
  rolledUp: string;
}

/**
 * Map from a parent-line index → its subtotal info, for parents that have children.
 * Leaf accounts (no children, hence no subtotal row) are absent from the map.
 *
 * Matches by an open-stack of parents so nested grandchildren work correctly, and so
 * two siblings that happen to share a name don't cross-pair.
 */
export function computeParentRanges(lines: ReportLineLike[]): Map<number, ParentInfo> {
  const parents = new Map<number, ParentInfo>();
  const stack: { idx: number; depth: number; name: string }[] = [];
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i]!;
    const depth = l.depth ?? 0;
    if (l.isSubtotal) {
      // Pop the matching open parent (same depth + name; LIFO so the nearest wins).
      for (let s = stack.length - 1; s >= 0; s--) {
        const top = stack[s]!;
        if (top.depth === depth && top.name === l.name) {
          parents.set(top.idx, { subtotalIdx: i, rolledUp: l.amount });
          stack.splice(s, 1);
          break;
        }
      }
    } else if (l.accountId) {
      // Tentatively open — if no matching subtotal ever closes it, it stays a leaf.
      stack.push({ idx: i, depth, name: l.name });
    }
  }
  return parents;
}

/** Every parent-with-children accountId in `lines` — for "Collapse all". */
export function collectParentIds(lines: ReportLineLike[]): string[] {
  const map = computeParentRanges(lines);
  const ids: string[] = [];
  for (const idx of map.keys()) {
    const id = lines[idx]?.accountId;
    if (id) ids.push(id);
  }
  return ids;
}
